import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import {
  getAllAudios,
  getAllAudiosByGenre,
  getAudioById,
  getAudioBySlug,
  getAudiosByAudioType,
  getAudiosByGenre,
  getAudiosByGenreName,
  getAudiosByLanguage,
  getPopularAudios,
  getSimilarAudios,
  likeAudio,
} from '../api/audiosApi';

// Hook for fetching all audios with filtering
export const useAudios = (filters = {}, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audios', filters],
    () => getAllAudios(filters),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audios';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled
    }
  );
};

// Hook for infinite loading of audios
export const useInfiniteAudios = (filters = {}, enabled = true) => {
  const { setError } = useAuthStore();
  const limit = filters.limit || 10;
  
  return useInfiniteQuery(
    ['infiniteAudios', filters],
    ({ pageParam = 0 }) => getAllAudios({ ...filters, offset: pageParam }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.data.length < limit) return undefined;
        return pages.length * limit;
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audios';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled
    }
  );
};

// Hook for fetching a single audio by ID
export const useAudio = (id, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audio', id],
    () => getAudioById(id),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audio';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: !!id && enabled
    }
  );
};

// Hook for fetching a single audio by slug
export const useAudioBySlug = (slug, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audio', 'slug', slug],
    () => getAudioBySlug(slug),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audio';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: !!slug && enabled
    }
  );
};

// Hook for fetching audios by genre
export const useAudiosByGenre = (genreId, params = {}, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audios', 'genre', genreId, params],
    () => getAudiosByGenre(genreId, params),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audios by genre';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: !!genreId && enabled
    }
  );
};

export const useAllAudiosByGenre = (genreId, params = {}, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audiosall', 'genre', genreId, params],
    () => getAllAudiosByGenre(genreId, params),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audios by genre';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: !!genreId && enabled
    }
  );
};
// Hook for fetching audios by language
export const useAudiosByLanguage = (language, params = {}, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audios', 'language', language, params],
    () => getAudiosByLanguage(language, params),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch audios by language';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: !!language && enabled
    }
  );
};

// Hook for fetching popular audios
export const usePopularAudios = (params = {}, enabled = true) => {
  const { setError } = useAuthStore();
  
  return useQuery(
    ['audios', 'popular', params],
    () => getPopularAudios(params),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch popular audios';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled
    }
  );
};



// Hook for liking an audio
export const useLikeAudio = () => {
  const { setError } = useAuthStore();
  const queryClient = useQueryClient();
  
  return useMutation(
    (id) => likeAudio(id),
    {
      onSuccess: (data, variables) => {
        // Update the audio cache optimistically
        queryClient.setQueryData(['audio', variables], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              likes_count: data.likes_count
            };
          }
          return oldData;
        });
        
        // Invalidate other queries that might be affected
        queryClient.invalidateQueries(['audios']);
        queryClient.invalidateQueries(['audios', 'popular']);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to like audio';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );
};

export const useSimilarAudios = (id, params = {}, enabled = true) => {
    const { setError } = useAuthStore();
    
    return useQuery(
      ['audios', 'similar', id, params],
      () => getSimilarAudios(id, params),
      {
        onError: (error) => {
          const errorMessage = error?.response?.data?.error || 'Failed to fetch similar audios';
          setError(errorMessage);
          toast.error(errorMessage);
        },
        retry: 1,
        staleTime: 300000, // 5 minutes
        cacheTime: 3600000, // 1 hour
        enabled: !!id && enabled
      }
    );
  };


  export const useAudiosByAudioType = (audioType, params = {}, enabled = true) => {
    const { setError } = useAuthStore();
    
    return useQuery(
      ['audios', 'audioType', audioType, params],
      () => getAudiosByAudioType(audioType, params),
      {
        onError: (error) => {
          const errorMessage = error?.response?.data?.error || 'Failed to fetch audios by audio type';
          setError(errorMessage);
          toast.error(errorMessage);
        },
        retry: 1,
        staleTime: 300000, // 5 minutes
        cacheTime: 3600000, // 1 hour
        enabled: !!audioType && enabled
      }
    );
  };

  export const useAudiosByGenreName = (genreName, filters = {}, enabled = true) => {
    const { setError } = useAuthStore();
    
    return useQuery(
      ['audiosByGenreName', genreName, filters],
      () => getAudiosByGenreName(genreName, filters),
      {
        onError: (error) => {
          const errorMessage = error?.response?.data?.error || 'Failed to fetch audios';
          setError(errorMessage);
          toast.error(errorMessage);
        },
        retry: 1,
        staleTime: 300000, // 5 minutes
        cacheTime: 3600000, // 1 hour
        enabled: !!genreName && enabled
      }
    );
  };